import Grid from '@mui/material/Grid';
import type { Resource } from '@openx/types/resources.ts';
import { groupHistoryEntries, mapAuditTrailToHistoryEntry } from '@openx/utils/history/mapAuditTrailToHistoryEntry.ts';
import type {
  GroupHistoryEntriesHandler,
  HistoryData,
  HistoryEntriesGroup,
  HistoryEntryData,
  HistoryEntryDataGrouped,
} from '@openx/utils/history/types.ts';
import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCoreContext } from '../../utils/context/CoreContext.tsx';
import { Button } from '../Button/Button.tsx';
import { Loader } from '../Loader/Loader.tsx';
import { Placeholder } from '../Placeholder/Placeholder.tsx';
import { HistoryEntryRevertDrawer } from './components/HistoryEntryRevert/HistoryEntryRevertDrawer.tsx';
import { useHistoryEntryRevert } from './components/HistoryEntryRevert/useHistoryEntryRevert.ts';
import { HistoryContainer } from './HistoryContainer.tsx';
import { HistoryEntry } from './HistoryEntry.tsx';
import type { HistoryProps } from './types.ts';

export function History<R extends Resource, G extends HistoryEntriesGroup = HistoryEntriesGroup, I = string>({
  mapAuditTrailsToHistoryData,
  customGroupHistoryEntries,
  customInitialRender,
  drawerMode,
  groups,
  hideEmptyCreateRows = false,
  historyRenderConfig,
  id,
  instanceUid,
  isLoading,
  labels,
  onRevert,
  resource,
  showAllCreateEntries,
  status,
  valuesMappings,
  historyEntryElementMap,
}: HistoryProps<R, G, I>): ReactElement {
  const { t } = useTranslation();
  const { useHistoryFetch } = useCoreContext();
  const [visibleTrailsCount, setVisibleCount] = useState(10);

  const { hasMore, loadMore, loading, trails } = useHistoryFetch<R>({
    id,
    instanceUid,
    isLoading,
    resource,
  });

  const {
    selectedAuditTrail,
    handleOpenRevertDrawer,
    handleCloseRevertDrawer,
    isRevertDrawerOpen,
    shouldShowRevertButton,
  } = useHistoryEntryRevert({
    trails,
  });

  const groupHistoryEntriesHandler: GroupHistoryEntriesHandler<G> = customGroupHistoryEntries || groupHistoryEntries;

  const historyEntriesData = useMemo<HistoryData>(() => {
    if (mapAuditTrailsToHistoryData) {
      return mapAuditTrailsToHistoryData(trails, historyRenderConfig);
    }

    const historyEntries: HistoryData = { create: [], update: [] };
    return trails
      .map(trail =>
        mapAuditTrailToHistoryEntry({
          auditTrail: trail,
          historyEntryElementMap,
          keepOriginalForCreate: !!customInitialRender,
          labels,
          valuesMappings,
        })
      )
      .filter(entry => Object.values(entry.what).length)
      .map(entry =>
        entry.mode === 'update' || entry.mode === 'expire' || !customInitialRender
          ? groupHistoryEntriesHandler(entry, groups)
          : entry
      )
      .reduce<HistoryData>((acc, record) => {
        if (record.mode === 'update' || record.mode === 'expire') {
          // only record with the mode 'create' can be a HistoryEntryData
          record.what.length && acc.update.push(record as HistoryEntryDataGrouped);
        } else {
          // all records with the mode 'create'
          // have the same type so either HistoryEntryData
          // (when customInitialRender is present) or HistoryEntryDataGrouped (otherwise)
          if (showAllCreateEntries) {
            acc.create.push(record as HistoryEntryData & HistoryEntryDataGrouped);
          } else {
            acc.create[0] = record as HistoryEntryData & HistoryEntryDataGrouped;
          }
        }
        return acc;
      }, historyEntries);
  }, [
    customInitialRender,
    groupHistoryEntriesHandler,
    groups,
    historyRenderConfig,
    labels,
    mapAuditTrailsToHistoryData,
    showAllCreateEntries,
    trails,
    valuesMappings,
    historyEntryElementMap,
  ]);

  const shouldLoadMore = useMemo(
    () => historyEntriesData.update.length < visibleTrailsCount && hasMore,
    [hasMore, historyEntriesData.update.length, visibleTrailsCount]
  );

  useEffect(() => {
    if (!trails.length || shouldLoadMore) {
      loadMore();
    }
  }, [loadMore, trails.length, shouldLoadMore]);

  const hasCreateHistory = !!historyEntriesData.create.length;
  const hasUpdateHistory = !!historyEntriesData.update.length;
  const noHistoryFound = !hasUpdateHistory && !hasCreateHistory;
  const shouldShowMoreChangesButton = hasMore || historyEntriesData.update.length > visibleTrailsCount;

  if (!loading && !hasMore && noHistoryFound) {
    return <Placeholder subtitle={t('No history found')} />;
  }

  return (
    <>
      {((loading && !hasUpdateHistory) || shouldLoadMore) && <Loader />}
      {hasUpdateHistory && (
        <HistoryContainer title={t('Change History')} divider={hasCreateHistory} titleSize={2} drawerMode={drawerMode}>
          <Grid size={10}>
            {historyEntriesData.update.slice(0, visibleTrailsCount).map((historyEntry, index) => (
              <HistoryEntry
                data={historyEntry}
                key={historyEntry.objectId + historyEntry.when}
                hideEmpty={false}
                drawerMode={drawerMode}
                handleOpenRevertDrawer={handleOpenRevertDrawer}
                shouldShowRevertButton={onRevert && shouldShowRevertButton(index, historyEntry.when, historyEntry.who)}
              />
            ))}
            {shouldShowMoreChangesButton && (
              <Grid container>
                <Grid size={2} />
                <Grid
                  size={9}
                  sx={{
                    ml: 4,
                  }}
                >
                  <Button
                    data-test="show-more-changes"
                    variant="text"
                    color="primary"
                    onClick={() => setVisibleCount(prevCount => prevCount + 10)}
                    loading={loading}
                  >
                    {t('Show more changes')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
        </HistoryContainer>
      )}
      {loading && !hasCreateHistory && <Loader />}
      {hasCreateHistory && (
        <HistoryContainer title={t('Initial Settings')} titleSize={2} status={status} drawerMode={drawerMode}>
          <Grid size={10}>
            {historyEntriesData.create.map((historyEntry: HistoryEntryData | HistoryEntryDataGrouped) => (
              <HistoryEntry
                customInitialRender={customInitialRender}
                // historyEntry with a type HistoryEntryData is only possible with customInitialRender function
                // this function will handle historyEntry passed as HistoryEntryData
                data={historyEntry as HistoryEntryDataGrouped}
                drawerMode={drawerMode}
                hideEmpty={hideEmptyCreateRows}
                key={historyEntry.objectId + historyEntry.when}
              />
            ))}
          </Grid>
        </HistoryContainer>
      )}

      {isRevertDrawerOpen && (
        <HistoryEntryRevertDrawer
          historyEntriesData={historyEntriesData.update}
          isOpen={isRevertDrawerOpen}
          onClose={handleCloseRevertDrawer}
          onRevert={onRevert}
          selectedAuditTrail={selectedAuditTrail}
        />
      )}
    </>
  );
}
